import { useEffect, useRef } from "react";

import { reportError } from "src/services/errorReporting";
import eventBus, { EVENTS } from "src/services/eventEmitter";

import { OPTION_TWIN_MUTATIONS } from "./optionTwinMutations";

/**
 * Query options for a page's own read of a design option. Group and Person
 * entries are keyed by id only, so a baseline read of the same team or person
 * (a members list, a breadcrumb, a title) would overwrite the option's values
 * in the normalized cache. `no-cache` keeps the option read out of it.
 */
export const OPTION_PAGE_READ_POLICY = { fetchPolicy: "no-cache" } as const;

/**
 * A `no-cache` read does not see mutation results, so refetch it whenever a
 * `*WithOption` write completes, including writes that skip the changelog
 * event. Inert when `active` is false. The listener is bound once per
 * `active`, and calls the latest `refetch`, because Apollo's refetch
 * identity is not stable and rebinding could miss an event. Bursts collapse
 * to one refetch. A failed refetch is reported: the page keeps showing
 * pre-write data.
 */
const REFETCH_BATCH_MS = 100;

export const useRefetchAfterOptionWrites = (
  refetch: () => unknown,
  active: boolean
): void => {
  const refetchRef = useRef(refetch);
  useEffect(() => {
    refetchRef.current = refetch;
  });

  useEffect(() => {
    if (!active) {
      return undefined;
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    let lastOperation = "";
    const handler = (payload?: { operationName?: string }) => {
      const operationName = payload?.operationName;
      if (!operationName || !OPTION_TWIN_MUTATIONS.has(operationName)) {
        return;
      }
      // A bulk move emits one event per twin. One refetch covers the burst.
      lastOperation = operationName;
      clearTimeout(timer);
      timer = setTimeout(() => {
        const afterOperation = lastOperation;
        Promise.resolve(refetchRef.current()).catch((error: unknown) => {
          reportError(error, {
            context: "useRefetchAfterOptionWrites",
            afterOperation,
          });
        });
      }, REFETCH_BATCH_MS);
    };
    eventBus.on(EVENTS.OPTION_WRITE_COMPLETED, handler);
    return () => {
      clearTimeout(timer);
      eventBus.off(EVENTS.OPTION_WRITE_COMPLETED, handler);
    };
  }, [active]);
};
